import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import NotificationAnalytics from '~/analytics/notification/notificationAnalytics'
import CheckmarkIcon from '~/components/Icons/svgs/CheckmarkIcon'
import CopyIcon from '~/components/Icons/svgs/CopyIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { useSetMarkAsReadMutation } from '~/redux/apis/notification-api'
import { NotificationMessage } from '~/shared/types/notification.types'

export default function FamilyDiscountNotification({
  message,
  id,
  isRead,
}: {
  message: NotificationMessage
  id: number
  isRead: boolean
}): React.ReactElement {
  const { t } = useTranslation('gamification')
  const [isCopied, setIsCopied] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [setMarkAsRead] = useSetMarkAsReadMutation()
  const discountCode = 'FAMILY30'

  const onCopyClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    setIsCopied(true)
    navigator.clipboard.writeText(discountCode)
    onClick(true)
    setTimeout(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setIsAnimating(false)
        setTimeout(() => {
          setIsCopied(false)
        }, 300)
      }, 2000)
    }, 100)
  }

  const onClick = (isCodeCopied: boolean): void => {
    NotificationAnalytics.logNotificationClick(message.title, { code_copied: isCodeCopied })
    if (!isRead) {
      setMarkAsRead({ notificationIds: [id] })
    }
  }

  return (
    <div className="relative flex px-2 pt-2" onClick={() => onClick(false)}>
      {isCopied && (
        <div
          className={`flex items-center absolute bg-black bg-opacity-80 text-white text-center rounded px-2 py-1 right-12 top-14 duration-300 ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <SVGIcon className="mr-1" icon={CheckmarkIcon} pathClassName="text-green" size={15} />
          {t('copied')}
        </div>
      )}
      <div className="flex flex-col bg-purple rounded-small p-2 min-w-25 h-full">
        <ResponsiveImage
          className="bg-white p-1 rounded-small w-full"
          height={100}
          imageWidths="100"
          srcKey="notifications/family-friends-discount-code-v2.png"
          width={100}
        />
      </div>
      <div className="w-full ml-2">
        <h3 className="font-semibold text-md leading-none"> {t('specialDiscount')}</h3>
        <p className="leading-tight my-1">{t('shareCode')}</p>
        <div className="w-44 justify-between bg-purple-light-deep rounded-medium px-2 py-1 mt-1">
          <p className="text-center leading-none mb-0.5">{discountCode}</p>
          <button
            className="flex items-center justify-center text-white rounded px-1.5 cursor-pointer w-40 duration-300 md:hover:bg-purple-dark bg-purple"
            onClick={onCopyClick}
          >
            {t('copy')} <SVGIcon className="ml-1" icon={CopyIcon} size={15} />
          </button>
        </div>
      </div>
      {!isRead && (
        <div className="absolute flex justify-center items-center right-3 -bottom-1.5">
          <span className="animate-ping absolute inline-flex rounded-full bg-purple-bright opacity-75 w-3.5 h-3.5" />
          <span className="absolute flex justify-center items-center rounded-full bg-purple w-2.5 h-2.5 text-xxs font-semibold" />
        </div>
      )}
    </div>
  )
}
